import { DeviceTypeEnum, ReadingViewModeEnum, SkillEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS, Step } from 'react-joyride';

import { useTestSubmissionStore } from '../../model/test-submissions.hook';
import { ReviewStatus } from '../../model/test-submissions.store';
import { updateUserTutorial } from './api/user-profile';
import { defaultGuidedTourProps } from './defaultGuidedTourProps';
import { getReadingGuidedTour } from './readingGuideTourStep.variables';

interface ReadingTabletGuidedTourProps {}

const scrollToButtonQuestionNavigation = () => {
  const scrollContainer = document.getElementById('body-container');
  const elm = document.getElementsByClassName('btn-question-navigation')[0] as HTMLElement;
  scrollContainer.scrollTop = elm.offsetTop;
};

export const ReadingTabletGuidedTour = observer((props: ReadingTabletGuidedTourProps) => {
  const [tourStepIndex, setTourStepIndex] = React.useState(0);
  const testSubmissionStore = useTestSubmissionStore();

  const guidedReadingTabletSteps: Step[] = getReadingGuidedTour({
    screenMode: 'tablet',
    minutes: Math.floor(testSubmissionStore.totalDuration / 60000),
    sectionCount: testSubmissionStore.testSections.length,
    isMobile: true,
  });

  React.useEffect(() => {
    testSubmissionStore.tourGuide.setTotalStep(guidedReadingTabletSteps.length);
  }, [testSubmissionStore.tourGuide, guidedReadingTabletSteps.length]);

  const tabletJoyRideMobileCallback = React.useCallback(
    (data) => {
      const { action, index, status, type } = data;
      testSubmissionStore.tourGuide.setTourIndex(index);
      const elm: any = document.getElementsByClassName('react-joyride__spotlight')?.[0];
      switch (index) {
        case 1: {
          if (elm) {
            const cloneTabletElm = elm.cloneNode(true);
            elm.parentNode.insertBefore(cloneTabletElm, elm);
            cloneTabletElm.style.left = '0px';
            cloneTabletElm.style.right = '0px';
            cloneTabletElm.style.width = '100%';
            cloneTabletElm.style.top = `${Number(elm.style.top.replace('px', '')) - 2}px`;
            cloneTabletElm.style.height = `${Number(elm.style.height.replace('px', '')) + 2}px`;
          }
          break;
        }
        case 2: {
          testSubmissionStore.goToFirstQuestionGroup();
          scrollToButtonQuestionNavigation();
          break;
        }
        case 3: {
          if (elm) {
            elm.style.left = '0px';
            elm.style.right = '0px';
            elm.style.width = '100%';
            elm.style.top = `${Number(elm.style.top.replace('px', '')) + 4}px`;
          }
          testSubmissionStore.goToLastQuestionGroup();
          testSubmissionStore.setReviewStatus(ReviewStatus.REVIEWING);
          scrollToButtonQuestionNavigation();
          break;
        }
        default:
      }
      if (type === EVENTS.STEP_AFTER) {
        let nextIndex = index;
        switch (action) {
          case ACTIONS.NEXT: {
            nextIndex += 1;
            break;
          }
          case ACTIONS.PREV: {
            nextIndex -= 1;
            break;
          }
        }
        setTourStepIndex(nextIndex);
      } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        testSubmissionStore.goToFirstQuestionGroup();
        updateUserTutorial({ skill: SkillEnum.READING, deviceType: DeviceTypeEnum.MOBILE });
        testSubmissionStore.tourGuide.finishTourGuide();
        testSubmissionStore.setReviewStatus(ReviewStatus.NOT_STARTED);
        testSubmissionStore.currentTestSection.updateCurrentViewMode(ReadingViewModeEnum.PASSAGE);
      }
    },
    [testSubmissionStore]
  );

  return (
    <Joyride
      {...defaultGuidedTourProps}
      spotlightPadding={0}
      disableScrollParentFix
      run={!testSubmissionStore.tourGuide.isTourGuideFinished}
      callback={tabletJoyRideMobileCallback}
      stepIndex={tourStepIndex}
      steps={guidedReadingTabletSteps}
    />
  );
});
